import { ApolloClient } from 'apollo-client';
import { InMemoryCache } from 'apollo-cache-inmemory';
import { HttpLink } from 'apollo-link-http';
import { onError } from 'apollo-link-error';
import { setContext } from 'apollo-link-context';
import { ApolloLink, Observable } from 'apollo-link';
import { saveStarted, saveFinished } from './saveStatus';
import { showToast } from './toast';
import { t } from './i18n';
import { prepareRecording, configureHistory } from './undoRedo';
import { identityProvider } from './utils';
import { bumpPreviewRefresh } from './previewRefresh';

// True when the GraphQL operation is a mutation (any cell edit -> refresh preview).
const isMutationOperation = (operation) => {
  const def = operation.query.definitions.find(
    (d) => d.kind === 'OperationDefinition'
  );
  return !!def && def.operation === 'mutation';
};

export const backEndUri = process.env.REACT_APP_TREEOSK_BACKEND_URI ? process.env.REACT_APP_TREEOSK_BACKEND_URI : 'http://localhost:4000';
export const latestPublicationsLink = process.env.REACT_APP_LATEST_PUBLICATIONS_LINK;
// Liens de partage : adresse publique sous laquelle une interface est visible (https://treeosk.co/preview/<nom>/).
export const shareBaseUrl = (process.env.REACT_APP_SHARE_BASE_URL || 'https://treeosk.co/preview/').replace(/\/?$/, '/');
// Application interne de planification des interventions (Base44), accessible depuis le menu.
export const interventionsUrl = process.env.REACT_APP_INTERVENTIONS_URL || 'https://treeosk-interventions-563556b7.base44.app';

let lastErrorToast = 0;

export const apolloClient = new ApolloClient({
  link: ApolloLink.from([
    onError(({ graphQLErrors, networkError, operation, forward }) => {
      if (graphQLErrors)
        graphQLErrors.map(({ message, locations, path }) =>
          console.log(
            `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`,
          ),
        );
      if (networkError) console.log(`[Network error]: ${networkError}`);
      // Une coupure ponctuelle (redémarrage du serveur, connexion qui saute) ne doit pas afficher d'erreur ni laisser
      // une page vide : on retente une fois, après une courte pause, les lectures qui ont échoué côté réseau.
      // Les écritures ne sont jamais rejouées (risque de doublons).
      const status = networkError && networkError.statusCode;
      const transient = networkError && (!status || status >= 500);
      if (transient && !isMutationOperation(operation) && !operation.getContext().retried) {
        operation.setContext({ retried: true });
        return new Observable((observer) => {
          let sub;
          const timer = setTimeout(() => { sub = forward(operation).subscribe(observer); }, 800);
          return () => { clearTimeout(timer); if (sub) sub.unsubscribe(); };
        });
      }
      // Message clair pour l'utilisateur (au plus un toutes les 6 s pour ne pas empiler les alertes).
      const now = Date.now();
      if ((networkError || (graphQLErrors && graphQLErrors.length)) && now - lastErrorToast > 6000) {
        lastErrorToast = now;
        showToast(
          networkError
            ? t('ui.err.network')
            : t('ui.err.generic'),
          'error',
          7000
        );
      }
    }),
    setContext(async (_, { headers }) => {
      // Si le jeton ne peut pas être lu/rafraîchi (Netlify Identity indisponible), on envoie la requête sans jeton
      // plutôt que de la faire échouer : le serveur ne s'en sert pas pour les lectures.
      let token = null;
      try { token = await identityProvider.currentUser().jwt(); } catch (e) { console.log(`[Auth] token unavailable: ${e}`); }
      return {
        headers: {
          ...headers,
          authorization: token ? `Bearer ${token}` : "",
        }
      }
    }),
    // Afterware: notify the inline live preview whenever a mutation completes,
    // so the preview iframe reloads on any cell edit (see previewRefresh.js).
    new ApolloLink((operation, forward) =>
      forward(operation).map((response) => {
        if (isMutationOperation(operation) && !(response.errors && response.errors.length)) {
          bumpPreviewRefresh();
        }
        return response;
      })
    ),
    // Suivi des enregistrements pour l'indicateur de sauvegarde de la barre du haut.
    new ApolloLink((operation, forward) => {
      if (!isMutationOperation(operation)) return forward(operation);
      saveStarted();
      // Annuler/Refaire : on note l'ancienne valeur avant l'envoi, on l'enregistre seulement si la mutation réussit.
      let recordIfOk = null;
      try { recordIfOk = prepareRecording(operation, apolloClient.cache); } catch (e) { /* jamais bloquant */ }
      return new Observable((observer) => {
        let failed = false;
        const sub = forward(operation).subscribe({
          next: (response) => {
            if (response.errors && response.errors.length) failed = true;
            observer.next(response);
          },
          error: (error) => { saveFinished(true); observer.error(error); },
          complete: () => { saveFinished(failed); if (!failed && recordIfOk) recordIfOk(); observer.complete(); },
        });
        return () => sub.unsubscribe();
      });
    }),
    new HttpLink({
      uri: `${backEndUri}/graphql`,
      credentials: 'include'
    })
  ]),
  cache: new InMemoryCache()
});
configureHistory({
  mutate: (mutation, variables) => apolloClient.mutate({ mutation, variables }),
  refresh: () => apolloClient.reFetchObservableQueries(),
});
identityProvider.on("logout", () => apolloClient.resetStore());

export const usersListMockData = process.env.NODE_ENV === 'development' ? [
      {
        "id": "2c1dce3a-dd76-400c-801a-65bd5a926bd7",
        "aud": "",
        "role": "",
        "email": "renaud+treeosk_user@26lights.com",
        "confirmed_at": "2019-05-27T14:16:30Z",
        "app_metadata": {
          "provider": "email",
          "roles": [
            "390df690-4e27-40f9-8a4a-40b570f90eca",
            "62cd61e7-79e5-4e5d-9384-f20e93e0de1f"
          ]
        },
        "user_metadata": {},
        "created_at": "2019-05-27T14:15:27Z",
        "updated_at": "2019-05-27T14:15:27Z"
      },
      {
        "id": "07d60d36-55fa-4b12-9cdc-41b77c4d8b2e",
        "aud": "",
        "role": "",
        "email": "jon@slantedmedia.be",
        "confirmed_at": "2019-05-16T08:08:43Z",
        "invited_at": "2019-04-29T10:24:33Z",
        "app_metadata": {
          "provider": "email",
          "roles": [
            "admin"
          ]
        },
        "user_metadata": {
          "full_name": "Jon"
        },
        "created_at": "2019-04-29T10:24:33Z",
        "updated_at": "2019-04-29T10:24:33Z"
      },
      {
        "id": "91c9da27-7285-4b37-bd43-e208f2a06abd",
        "aud": "",
        "role": "",
        "email": "renaud@26lights.com",
        "confirmed_at": "2019-04-29T10:23:56Z",
        "invited_at": "2019-04-29T09:26:40Z",
        "recovery_sent_at": "2019-04-29T09:59:35Z",
        "app_metadata": {
          "provider": "email",
          "roles": [
            "admin"
          ]
        },
        "user_metadata": {
          "full_name": "Renaud"
        },
        "created_at": "2019-04-29T09:26:40Z",
        "updated_at": "2019-04-29T09:26:40Z"
      }
    ] : undefined;
